import {
  put,
  call,
  select,
  take,
  fork,
  cancel,
  cancelled,
  SagaReturnType,
} from 'redux-saga/effects'
import type { Task } from 'redux-saga'
import Moment from 'moment-timezone'
import moment from 'moment'

import AppointmentBookingActions, {
  AppointmentBookingTypes,
  DEFAULT_TIMES,
} from 'APP/Redux/AppointmentBookingRedux'
import { FetchAvailabilitiesRequestPayload } from 'APP/Components/AppointmentBooking/utils/types'
import { BOOKING_FLOW } from 'APP/Components/AppointmentBooking/constants'

// Services
import { logger } from 'APP/Lib/Datadog'
import {
  createAppointment,
  fetchAvailabilitiesExists,
  FetchAvailabilitiesExistsParams,
} from 'APP/Store/utils/timekeeper'
import {
  fetchAvailabilitiesV2 as fetchAvailabilitiesV2FromSilkroad,
  fetchTimeBasedAvailabilitiesV3 as fetchTimeBasedAvailabilitiesV3FromSilkroad,
} from 'APP/Store/utils/silkroad'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import { CreateAppointmentPayload } from 'APP/Store/AppointmentBooking/types'

export function* getAvailableAppointments(action: {
  payload: FetchAvailabilitiesRequestPayload
}): Generator {
  const { payload } = action
  const { bookingId, day, params } = payload
  try {
    const startAt = moment(params.start_at).format()
    const endAt = moment(params.start_at).endOf('day').format()

    const { enableAvailabilitiesV3TimeBasedSearch } = yield select(selectFeatures)

    if (enableAvailabilitiesV3TimeBasedSearch) {
      const timesByDate: SagaReturnType<typeof fetchTimeBasedAvailabilitiesV3FromSilkroad> =
        yield call(fetchTimeBasedAvailabilitiesV3FromSilkroad, {
          bookingId,
          dateFrom: startAt,
          dateUntil: endAt,
          // The window is a device-local day, so v3 groups it in the device's timezone too.
          timezone: Moment.tz.guess(),
        })

      if (yield cancelled()) {
        return
      }
      // Each day goes in as v3 labels it (YYYY-MM-DD). v3 leaves out a day with no times,
      // so the requested day starts empty.
      const days = { [moment(day).format('YYYY-MM-DD')]: DEFAULT_TIMES, ...timesByDate }
      for (const [date, times] of Object.entries(days)) {
        yield put(AppointmentBookingActions.getAvailableAppointmentsSuccess({ times, day: date }))
      }
      return
    }

    const result: SagaReturnType<typeof fetchAvailabilitiesV2FromSilkroad> = yield call(
      fetchAvailabilitiesV2FromSilkroad,
      {
        bookingId,
        dateFrom: startAt,
        dateUntil: endAt,
        experience: BOOKING_FLOW.TIME_BASED,
      },
      // Only the slots are read: skip the practitioner and licenses
      false,
      false
    )

    if (yield cancelled()) {
      return
    }
    yield put(
      AppointmentBookingActions.getAvailableAppointmentsSuccess({
        // The time-based search always returns its slots grouped by part of day.
        times: result.availabilitiesPerPartsOfDay ?? DEFAULT_TIMES,
        day,
      })
    )
  } catch (error: unknown) {
    yield put(AppointmentBookingActions.getAvailableAppointmentsError(error, Moment(day)))
    logger.error('Failed to get available appointments', {}, error)
  } finally {
    if (yield cancelled()) {
      logger.info('getAvailableAppointments saga cancelled', { payload })
    }
  }
}

/**
 * Manages the lifecycle of availability requests. This is to ensure that
 * in-flight request cannot rehydrate the shared availabilities store
 * after an appointment has been scheduled. The implications of which
 * could be a provider being scheduled for an appointment that
 * they are not licensed to oversee or fit the requirements
 * of their provider availability constraints, shift segment,
 * or schedule
 *
 * {@see}: https://redux-saga.js.org/docs/advanced/TaskCancellation
 */
export function* watchGetAvailableAppointments(): Generator {
  let workers: Task[] = []

  const endFlowTypes: string[] = [
    AppointmentBookingTypes.CREATE_APPOINTMENT_SUCCESS,
    AppointmentBookingTypes.RESET_STATE,
    AppointmentBookingTypes.RESET_CREATE_APPOINTMENT,
  ]

  while (true) {
    const action = (yield take([
      AppointmentBookingTypes.GET_AVAILABLE_APPOINTMENTS,
      ...endFlowTypes,
    ])) as { type: string; payload: FetchAvailabilitiesRequestPayload }

    workers = workers.filter((task) => task.isRunning())

    if (action.type === AppointmentBookingTypes.GET_AVAILABLE_APPOINTMENTS) {
      const task: Task = yield fork(getAvailableAppointments, { payload: action.payload })
      workers.push(task)
      continue
    }

    for (const task of workers) {
      logger.info('Cancelling in-flight availability request', {
        actionType: action.type,
        taskId: task,
      })
      yield cancel(task)
    }
    workers = []
  }
}

export function* getAvailabilitiesExists({
  payload,
}: {
  payload: FetchAvailabilitiesExistsParams
}): Generator {
  try {
    const availabilitiesExists: SagaReturnType<typeof fetchAvailabilitiesExists> = yield call(
      fetchAvailabilitiesExists,
      { bookingId: payload.bookingId }
    )

    yield put(AppointmentBookingActions.getAvailabilitiesExistsSuccess(availabilitiesExists))
  } catch (error: unknown) {
    yield put(AppointmentBookingActions.getAvailabilitiesExistsError(error))
    logger.error('Failed to check if availabilities exist', {}, error)
  }
}

export function* bookAppointment({ payload }: { payload: CreateAppointmentPayload }): Generator {
  try {
    const appointment = (yield call(createAppointment, payload)) as SagaReturnType<
      typeof createAppointment
    >

    if (!appointment?.id) {
      throw new Error('Appointment creation failed: Missing appointment ID in response')
    }

    yield put(AppointmentBookingActions.createAppointmentSuccess(appointment))

    logger.info('createAppointment succeeded', {
      appointment_id: appointment.id,
    })
  } catch (error) {
    yield put(AppointmentBookingActions.createAppointmentError(error))
    logger.error(
      'Failed to book appointment',
      {
        provider_id: payload?.provider_id,
        start_at: payload?.start_at,
        end_at: payload?.end_at,
        booking_id: payload?.bookingId,
      },
      error
    )
  }
}
